import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { IDENTITY_ROTATION } from '~/domain/viewport/frameRotation';
import { Environment } from '~/engine/data/Environment';
import type { DatasetEnvironmentRegistry } from '~/engine/scene/DatasetEnvironmentRegistry';
import type { ViewManager } from '~/engine/view/ViewManager';
import type { ViewportId } from '~/viewportGrid/layout/types';
import { ChangeNotifier } from '~/viewportGrid/adapters/changeSource';
import {
    EnvironmentAdapter,
    FrameRotationSource,
    RenderAdapter,
} from '~/viewportGrid/adapters/types';
import { PlaneViewRenderAdapter } from './PlaneViewRenderAdapter';

/**
 * Engine-backed {@link EnvironmentAdapter}. Hands the grid one
 * {@link RenderAdapter} per tile, each a {@link PlaneView} of a shared,
 * refcounted {@link Environment} resolved through the
 * {@link DatasetEnvironmentRegistry}. A row's oblique frame is that
 * Environment's, read and reset through the {@link ViewManager} that turns
 * the Environment's views.
 *
 * Wires the registry's environment factory ONCE at construction: minting an
 * Environment uploads the dataset's image on first acquire; the registry keeps
 * exactly one per distinct series, so tiles of the same dataset share one image
 * texture + SDF and cannot diverge. Wires the registry's residents into the
 * view manager at the same time, as the live Environments it re-transforms
 * and resets.
 */
export class EnvironmentBackedEnvironmentAdapter implements EnvironmentAdapter {
    private readonly frameSources = new Map<string, FrameRotationSource>();

    public constructor(
        private readonly viewManager: ViewManager,
        private readonly registry: DatasetEnvironmentRegistry
    ) {
        registry.setEnvironmentFactory((entry) => {
            const env = new Environment(viewManager);
            // Order is load-bearing: setStructureSet re-enters setImage and
            // drops any dose; setDose re-enters setStructureSet; setImage
            // rebuilds the object graph and drops any secondary image. So
            // structure set (which uploads the image itself) or else image,
            // then dose, then secondary image is the only order that leaves
            // every applied layer standing.
            const structureSet =
                entry.structureSet ?? entry.reference?.structureSet;
            if (structureSet) {
                // setStructureSet uploads the image and the ROI SDFs; the engine
                // then cross-sections each ROI per plane automatically.
                env.setStructureSet(structureSet);
            } else {
                env.setImage(entry.image);
            }
            if (entry.dose) {
                const doseStructureSet = entry.dose.structureSet ?? undefined;
                if (doseStructureSet !== entry.structureSet) {
                    throw new Error(
                        "Cannot load this dataset's dose: the dataset's " +
                            'dose references a different structure set.'
                    );
                }
                env.setDose(entry.dose);
            } else if (entry.reference?.dose) {
                env.setDose(entry.reference.dose);
            }
            if (entry.secondaryImage) {
                env.fuseSecondaryImage(
                    entry.secondaryImage,
                    entry.registration
                );
            }
            env.applyBlendSettings(entry.blend);
            return env;
        });
        viewManager.setResidentEnvironments(() =>
            registry.residentEnvironments()
        );
    }

    public createTileRenderer(tileId: ViewportId): RenderAdapter {
        return new PlaneViewRenderAdapter(
            this.viewManager,
            this.registry,
            tileId
        );
    }

    public releaseTileRenderer(renderer: RenderAdapter): void {
        renderer.dispose();
    }

    public frameRotationSourceFor(
        seriesInstanceUid: SeriesInstanceUid
    ): FrameRotationSource {
        const existing = this.frameSources.get(seriesInstanceUid);
        if (existing) {
            return existing;
        }
        const created = this.createFrameSource(seriesInstanceUid);
        this.frameSources.set(seriesInstanceUid, created);
        return created;
    }

    /** A no-op while the series has no resident Environment: nothing is turned. */
    public resetFrameRotation(seriesInstanceUid: SeriesInstanceUid): void {
        const environment = this.registry.environmentFor(seriesInstanceUid);
        if (environment) {
            this.viewManager.setFrameRotation(environment, IDENTITY_ROTATION);
        }
    }

    /**
     * The row's frame source. Its notifier follows the frame of the series'
     * resident Environment only while someone listens. An Environment is
     * disposed at zero references and re-minted on re-acquire, and a
     * re-minted one starts patient-aligned, so the notifier also follows the
     * registry's lifecycle: on each change it moves its frame subscription
     * to the resident Environment and tells the readers to re-read.
     */
    private createFrameSource(
        seriesInstanceUid: SeriesInstanceUid
    ): FrameRotationSource {
        let followed: Environment | undefined;
        let unsubscribeFrame: (() => void) | undefined;
        let unsubscribeLifecycle: (() => void) | undefined;
        /** Moves the frame subscription to the resident Environment; whether it moved. */
        const followResident = (): boolean => {
            const environment = this.registry.environmentFor(seriesInstanceUid);
            if (environment === followed) {
                return false;
            }
            unsubscribeFrame?.();
            followed = environment;
            unsubscribeFrame = environment
                ? this.viewManager.subscribeFrameRotation(environment, () =>
                      notifier.notify()
                  )
                : undefined;
            return true;
        };
        const notifier = new ChangeNotifier({
            onFirstSubscribe: () => {
                followResident();
                unsubscribeLifecycle = this.registry.subscribeLifecycle(() => {
                    if (followResident()) {
                        notifier.notify();
                    }
                });
            },
            onLastUnsubscribe: () => {
                unsubscribeLifecycle?.();
                unsubscribeLifecycle = undefined;
                unsubscribeFrame?.();
                unsubscribeFrame = undefined;
                followed = undefined;
            },
        });
        return {
            subscribe: notifier.subscribe,
            getVersion: notifier.getVersion,
            getFrameRotation: () => {
                const environment =
                    this.registry.environmentFor(seriesInstanceUid);
                return environment
                    ? this.viewManager.frameRotationOf(environment)
                    : IDENTITY_ROTATION;
            },
        };
    }
}
